import type { NostrMetadata } from '@nostrify/nostrify';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';

/**
 * Fetch kind 0 metadata for a set of pubkeys in a single relay round-trip.
 * Returns a map keyed by pubkey; missing profiles are simply absent.
 */
export function useProfilesMap(pubkeys: string[]) {
  const { nostr } = useNostr();
  const key = [...pubkeys].sort();

  return useQuery({
    queryKey: ['nostrss', 'profiles', key],
    queryFn: async (c) => {
      const events = await nostr.query([{ kinds: [0], authors: key }], { signal: c.signal });

      const latest = new Map<string, { createdAt: number; metadata: NostrMetadata }>();
      for (const event of events) {
        const existing = latest.get(event.pubkey);
        if (existing && existing.createdAt >= event.created_at) continue;
        try {
          latest.set(event.pubkey, {
            createdAt: event.created_at,
            metadata: JSON.parse(event.content) as NostrMetadata,
          });
        } catch {
          // Ignore malformed profile content.
        }
      }

      const map = new Map<string, NostrMetadata>();
      for (const [pubkey, entry] of latest) {
        map.set(pubkey, entry.metadata);
      }
      return map;
    },
    enabled: key.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}
